import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";
import Breadcrumbs from "../components/Breadcrumbs";
import CTAButton from "../components/CTAButton";
import { Reveal } from "../components/Reveal";
import { usePageMeta } from "../lib/seo";
import { getGuide } from "../lib/guides";
import { SITE } from "../lib/site";

export default function GuidePage() {
  const { slug } = useParams();
  const guide = getGuide(slug);

  usePageMeta({
    title: guide ? guide.title : "Guide not found",
    description: guide ? guide.excerpt : "CareCraft guides for Los Angeles families.",
    path: `/resources/${slug}`,
  });

  if (!guide) {
    return (
      <section className="bg-warmwhite pt-[72px]">
        <div className="mx-auto max-w-site px-4 py-24 sm:px-6 lg:px-12">
          <h1 className="font-serif text-4xl font-medium text-forest">Guide not found</h1>
          <p className="mt-4 text-charcoal/75">The guide you're looking for isn't available yet.</p>
          <Link
            to="/resources"
            data-testid="guide-not-found-back"
            className="mt-8 inline-flex items-center gap-2 font-medium text-forest hover:text-clay"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Back to Resources
          </Link>
        </div>
      </section>
    );
  }

  return (
    <>
      <section className="bg-ivory pt-[72px]">
        <div className="mx-auto max-w-site px-4 pb-14 pt-10 sm:px-6 md:pt-14 lg:px-12">
          <Breadcrumbs trail={[{ label: "Resources", to: "/resources" }, { label: guide.title }]} />
          <div className="mt-10 max-w-3xl">
            <p className="text-[0.7rem] font-semibold uppercase tracking-[0.2em] text-clay">
              CareCraft guide • {guide.minutes} min read
            </p>
            <h1 className="mt-6 font-serif text-4xl font-medium leading-[1.15] tracking-tight text-forest sm:text-5xl lg:text-[3.2rem]">
              {guide.title}
            </h1>
            <Reveal delay={0.3}>
              <p className="mt-7 text-lg leading-[1.7] text-charcoal/80 md:text-xl">{guide.excerpt}</p>
            </Reveal>
          </div>
        </div>
      </section>

      <article className="bg-warmwhite py-16 md:py-20">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-12">
          {guide.sections.map((s, i) => (
            <Reveal key={s.h} delay={0.03 * i}>
              <section className="mb-12">
                <h2 className="font-serif text-2xl font-medium tracking-tight text-forest md:text-[1.8rem]">
                  {s.h}
                </h2>
                {s.p &&
                  s.p.map((para, j) => (
                    <p key={j} className="mt-5 leading-[1.75] text-charcoal/85">
                      {para}
                    </p>
                  ))}
                {s.list && (
                  <ul className="mt-6 space-y-3.5">
                    {s.list.map((item) => (
                      <li key={item} className="flex gap-3.5 leading-[1.65] text-charcoal/85">
                        <Check className="mt-1 h-4 w-4 shrink-0 text-clay" aria-hidden="true" />
                        {item}
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            </Reveal>
          ))}

          <Reveal>
            <div className="grain rounded-2xl bg-forest p-8 text-ivory md:p-10">
              <h2 className="font-serif text-2xl font-medium leading-snug md:text-3xl">
                Let's find the right way forward, together.
              </h2>
              <p className="mt-4 max-w-xl text-ivory/80">
                Start by telling us what's happening. We'll listen first, then help you understand
                what options may make sense. Our guidance is complimentary to families.
              </p>
              <div className="mt-7 flex flex-wrap gap-4">
                <CTAButton to="/contact" variant="ivory" testId="guide-final-cta">
                  Talk With Doris
                </CTAButton>
                <a
                  href={SITE.phoneHref}
                  data-testid="guide-phone"
                  className="inline-flex min-h-[48px] items-center font-medium text-ivory/90 transition-colors hover:text-clay"
                >
                  Or call {SITE.phoneDisplay}
                </a>
              </div>
            </div>
            <Link
              to="/resources"
              data-testid="guide-back-link"
              className="mt-10 inline-flex items-center gap-2 font-medium text-forest transition-colors hover:text-clay"
            >
              <ArrowLeft className="h-4 w-4" aria-hidden="true" /> All guides &amp; FAQ
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          </Reveal>
        </div>
      </article>
    </>
  );
}
